import { useId, useState } from "react";
import { TimelineChart } from "../charts";
import { Activity, AlertCircle, CheckCircle2, Cloud, Cpu, Monitor, ShieldCheck, Smartphone, Zap } from "lucide-react";
import { Badge, Button, Dialog, DialogActions, SegmentedControl, SummaryMetrics, Table, type ThemeMode } from "@studio";
import { errorMessage } from "../lib/api";
import { ago, deviceState, fmtDate, fmtDateTime, fmtTime } from "../lib/format";
import { native, type LocalStatus } from "../lib/native";
import { useCustomerStatus, useMachine, useStatusTimeline } from "../lib/queries";
import { ConnectionTopology, NetworkMachineCard, type StatusMachine as NetMachine } from "@studio-features/meta-crew/system-network";
import { SearchPerformanceSummary } from "./analytics";
import { OrganizationIdentity } from "@studio-features/meta-crew/console-identity";
import { DataHeading, useThemeMode } from "../console/freshness";
import type { CustomerStatusView, Readiness, StatusMachine, StatusTimeline } from "../lib/types";
import { Empty, ErrorBox, InfoRow, Loading, Panel, Tabs } from "../console/ui";
import { StatusHistoryPanel } from "./monitoring";
import { MetricGrid } from "../console/metrics";

/**
 * 세 화면면 개편 (시안 d074369 surface-status.tsx 를 실제 데이터로).
 * 고객 웹 현황·머신 상세와 데스크톱 현황·머신이 같은 부품을 쓴다. 작업 내용·건수는 없다 (데스크톱은 처리 건수만).
 */

export const readinessText: Record<Readiness, [string, string]> = {
  ready: ["준비됨", "teal"],
  unpaired: ["미연결", "neutral"],
  paused: ["배정 중지", "amber"],
  offline: ["연결 끊김", "coral"],
  llm_not_installed: ["AI 설치 필요", "coral"],
  llm_login_required: ["AI 로그인 필요", "amber"],
  llm_throttled: ["AI 한도 도달", "amber"],
  no_device: ["디바이스 없음", "amber"],
  device_attention: ["디바이스 확인 필요", "amber"],
};
const issueText = (m: { readiness: Readiness; issues: Readiness[] }) => m.issues.map((i) => readinessText[i][0]).join(" · ") || readinessText[m.readiness][0];
const aiText = (m: StatusMachine) => (!m.connected ? "확인 불가" : m.ai.throttledUntil ? "한도 도달" : m.ai.loginRequired ? "로그인 필요" : m.ai.available ? "사용 가능" : "확인 필요");

function ServiceBlock({ s, title }: { s: CustomerStatusView; title: string }) {
  const sub = s.service.subscription;
  // 시안 6a084bb: 조직 프로필(이니셜·이름·이용 상태) + 문의처. 색만으로 상태를 전하지 않는다.
  return (
    <div className="lwi-service-overview">
      <OrganizationIdentity name={title} initials={title.trim().slice(0, 1) || "?"} state={s.service.status} subtitle={sub ? `${sub.planName ?? sub.planCode} · ${fmtDate(sub.currentPeriodEnd)}까지 · 구독 ${sub.daysLeft}일 남음` : "플랜 정보 없음"} />
      <div className="lwi-support">
        <span>서비스 문의</span>
        <strong>{s.service.support ?? "담당자에게 문의해 주세요"}</strong>
      </div>
    </div>
  );
}

/** 고객 웹 "현황" / 데스크톱 "현황". */
export function SystemStatusView({
  customerId,
  customerName,
  desktop = false,
  local,
  onNavigate,
  onMachine,
  canAnalyze = false,
}: {
  customerId: string;
  customerName: string;
  desktop?: boolean;
  canAnalyze?: boolean;
  local?: LocalStatus | null;
  onNavigate: (screen: string, search?: Record<string, string>) => void;
  onMachine?: (machineId: string) => void;
}) {
  const q = useCustomerStatus(customerId);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  const s = q.data;
  const t = s.totals;
  const me = desktop ? s.machines[0] : undefined;
  // 데스크톱은 로컬 엔진이 보는 소켓 상태가 가장 정확하다 (서버가 끊기면 서버 값은 오래된 값).
  const offline = desktop ? (local ? local.socket !== "connected" : !me?.connected) : false;
  return (
    <div className="lws-stack">
      <ServiceBlock s={s} title={desktop ? (me?.label ?? "이 PC") : customerName} />
      {offline && (
        <div className="mcc-banner">
          <Cloud size={18} />
          <div>
            <strong>서버에 다시 연결하고 있습니다</strong>
            <p>마지막 수신 {fmtTime(local?.lastReceivedAt ?? me?.lastSeenAt)} · 연결이 복구되면 상태가 자동으로 갱신됩니다. 작업 처리와 이벤트 보관은 이 PC에서 계속됩니다.</p>
          </div>
        </div>
      )}
      <div className={offline ? "mcc-stale" : ""}>
        {!desktop && <DataHeading title="시스템 상태" />}
        {!desktop ? (
          <>
            <SystemNetwork s={s} customerId={customerId} onMachine={onMachine ?? (() => onNavigate("machines"))} onNavigate={onNavigate} />
            {canAnalyze && <SearchPerformanceSummary customerId={customerId} tracking={t.seoTracking} onOpen={() => onNavigate("marketing")} />}
          </>
        ) : (
        me && (
          <SummaryMetrics
            items={(() => {
              // 데스크톱은 이 PC 의 로컬 엔진 값이 가장 정확하다 (서버 값은 마지막 하트비트 기준). 없으면 서버 값.
              const lt = local?.codex.throttledUntil && Date.parse(local.codex.throttledUntil) > Date.now();
              const aiValue = local ? (local.codex.installed === false ? "설치 필요" : lt ? "한도 도달" : local.codex.loggedIn ? "사용 가능" : "로그인 필요") : aiText(me);
              const aiOk = local ? local.codex.loggedIn && !lt : me.ai.available && !me.ai.throttledUntil;
              const devReady = local ? local.devices.filter((d) => d.state === "device").length : me.devices.ready;
              const devAll = local ? local.devices.length : me.devices.attached;
              const running = local?.runningTasks ?? me.runningTasks ?? 0;
              return [
                { label: "서버 연결", value: offline ? "재연결 중" : "연결됨", icon: <Cloud />, tone: offline ? "amber" : "teal" },
                { label: "AI", value: aiValue, icon: <Zap />, tone: aiOk ? "teal" : "amber" },
                { label: "준비된 디바이스", value: `${devReady} / ${devAll}`, icon: <Smartphone />, tone: devReady < devAll || !devAll ? "amber" : "teal" },
                { label: "처리 상태", value: running ? `처리 중 ${running}건` : "대기", icon: <Activity /> },
              ];
            })()}
          />
        )
        )}
        {!desktop ? null : s.machines.length === 0 ? (
          <Panel title="현장 PC 연결">
            <div className="mcc-empty">
              <Monitor />
              <h3>연결된 머신이 없습니다</h3>
              <p>현장 PC 설치와 연결은 담당자가 진행합니다.</p>
            </div>
          </Panel>
        ) : me ? (
          <>
              <ConnectionTopology machines={[networkMachine(me)]} local coreConnected={!offline} onSelect={() => onNavigate("machines")} />
            <Panel title="프로그램 · 서버">
              <InfoRow
                title={`프로그램 ${local?.appVersion ? `v${local.appVersion}` : me.version ? `v${me.version}` : ""} · ${local?.engine?.running === false ? "엔진 중지" : "실행 중"}`}
                description={[
                  local?.engine?.uptimeSec !== undefined ? `가동 ${Math.floor(local.engine.uptimeSec / 3600)}시간 ${Math.floor((local.engine.uptimeSec % 3600) / 60)}분` : "",
                  local?.reconnects24h !== undefined ? `최근 24시간 재연결 ${local.reconnects24h}회` : "",
                ]
                  .filter(Boolean)
                  .join(" · ") || `마지막 수신 ${ago(me.lastSeenAt)}`}
              >
                <Cpu size={18} />
              </InfoRow>
              {local?.outboxPending !== undefined && (
                <InfoRow
                  title={`미전송 이벤트 ${local.outboxPending}건`}
                  description={local.outboxPending ? "이 PC에서 보관하며 연결 복구 후 순서대로 전송합니다" : `마지막 수신 ${fmtTime(local.lastReceivedAt)}`}
                />
              )}
              <InfoRow
                title={me.ai.throttledUntil ? "AI 한도 도달" : me.ai.loginRequired ? "AI 로그인 필요" : me.ai.available ? "AI 로그인됨 · 사용 가능" : "AI 상태 확인 필요"}
                description={me.ai.throttledUntil ? `복구 예상 ${fmtDateTime(me.ai.throttledUntil)} · 복구 후 자동으로 준비 상태를 확인합니다` : "인증 상태와 사용 가능 여부만 표시합니다"}
              >
                {native() && (
                  <Button size="sm" variant="secondary" onClick={() => onNavigate("settings")}>
                    이 PC 설정
                  </Button>
                )}
              </InfoRow>
            </Panel>
            <DeviceCards customerId={customerId} machineId={me.id} local={local} />
            <StatusHistoryPanel path={`/c/${customerId}/machines/${me.id}/status-history`} title="24시간 연결 이력" />
          </>
        ) : null}
      </div>
    </div>
  );
}

/* ---------------- 시스템 상태 (시안 4572e49 system-network.tsx 를 실제 데이터로) ---------------- */

const issueGuide: Record<string, string> = {
  offline: "PC 전원과 인터넷 연결을 확인하세요.",
  device_attention: "디바이스에서 USB 디버깅 허용을 눌러 주세요.",
  no_device: "PC에 디바이스를 연결해 주세요.",
  llm_login_required: "이 PC에서 AI 계정에 로그인해 주세요.",
  llm_throttled: "AI 한도 복구 시각 이후 자동으로 준비 상태를 확인합니다.",
  unpaired: "담당자에게 PC 연결을 요청해 주세요.",
  paused: "담당자에게 일시 중지 상태를 확인해 주세요.",
  llm_not_installed: "이 PC에 AI 실행 도구를 설치해 주세요.",
};

/** 시안 부품(ConnectionTopology·NetworkMachineCard)이 받는 모양으로. 버전이 없으면 null (시안이 빈 버전을 숨긴다). */
function networkMachine(m: StatusMachine): NetMachine {
  return { ...m, version: m.version ?? null, devices: { ...m.devices, list: m.devices.list ?? [] } };
}

/** 고객 현황 › 시스템 상태: 종합 상태 한 줄, KPI 3개, 코어 → PC → 디바이스 구조도, 머신 카드, 24시간 연결 구간. */
function SystemNetwork({
  s,
  customerId,
  onMachine,
  onNavigate,
}: {
  s: CustomerStatusView;
  customerId: string;
  onMachine: (machineId: string) => void;
  onNavigate?: (screen: string, search?: Record<string, string>) => void;
}) {
  const timeline = useStatusTimeline(customerId);
  const machines = s.machines.map(networkMachine);
  const byLabel = (label: string) => {
    const m = s.machines.find((x) => x.label === label);
    if (m) onMachine(m.id);
  };
  const attention = s.attention;
  const severe = attention.some((a) => a.issues.some((i) => ["offline", "llm_login_required", "no_device", "unpaired", "llm_not_installed"].includes(i)));
  const state = !machines.length ? "연결 대기" : severe ? "조치 필요" : attention.length ? "주의" : "정상";
  const tl = timeline.data;
  const segs = tl?.machines.flatMap((m) => m.segments) ?? [];
  const span = (x: { from: string; to: string }) => Date.parse(x.to) - Date.parse(x.from);
  const duration = segs.reduce((n, x) => n + span(x), 0);
  const up = segs.filter((x) => x.connected).reduce((n, x) => n + span(x), 0);
  const t = s.totals;
  return (
    <div className="ln-stack">
      <div className={`ln-health ${attention.length ? "is-attention" : ""}`} role="status">
        {attention.length ? <AlertCircle size={22} /> : <CheckCircle2 size={22} />}
        <div>
          <strong>{state}</strong>
          <span>
            {!machines.length
              ? "현장 PC 연결을 기다리고 있습니다."
              : attention.length
                ? attention
                    .map((a) => {
                      const m = s.machines.find((x) => x.id === a.machineId);
                      return `${a.label} ${a.readiness === "offline" ? `연결 끊김 · 마지막 수신 ${ago(m?.lastSeenAt)}` : readinessText[a.readiness][0]}`;
                    })
                    .join(" · ")
                : "모든 PC와 디바이스가 연결되어 있습니다."}
          </span>
          {attention.length > 0 && <p>{[...new Set(attention.flatMap((a) => a.issues))].map((i) => issueGuide[i] ?? "담당자에게 상태를 확인해 주세요.").join(" ")}</p>}
        </div>
      </div>
      {/* 카드를 누르면 머신 목록으로 (2026-10-21). 끊긴 머신이 있으면 그 필터로 연다 */}
      <MetricGrid
        columns={3}
        items={[
          {
            label: "온라인 머신",
            value: `${t.online} / ${t.machines}`,
            icon: <Monitor />,
            tone: t.online === t.machines ? "teal" : "amber",
            onOpen: onNavigate && (() => onNavigate("machines", t.online < t.machines ? { filter: "disconnected" } : {})),
            openLabel: t.online < t.machines ? "연결 끊긴 머신 보기" : "머신 목록에서 보기",
          },
          {
            label: "준비된 디바이스",
            value: `${t.devicesReady} / ${t.devicesAttached}`,
            icon: <Smartphone />,
            note: "연결된 PC의 준비 상태",
            tone: t.devicesAttention ? "amber" : "teal",
            onOpen: onNavigate && (() => onNavigate("machines")),
            openLabel: "머신 목록에서 폰 상태 보기",
          },
          { label: "24시간 가동률", value: duration ? `${((up / duration) * 100).toFixed(1)}%` : "—", icon: <Activity />, note: `기록된 연결 시간 기준 · 끊김 ${t.disconnects24h}회` },
        ]}
      />
        <ConnectionTopology machines={machines} onSelect={byLabel} />
      {machines.length > 0 && (
        <>
            <div className="ln-machine-cards">
              {machines.slice(0, machines.length >= 10 ? 4 : undefined).map((m) => (
                <NetworkMachineCard key={m.id} machine={m} onSelect={byLabel} />
              ))}
            </div>
          {tl ? <ConnectionTimeline timeline={tl} /> : timeline.isLoading ? <Loading /> : null}
        </>
      )}
    </div>
  );
}

const kst = (iso: string | null) => (iso ? new Date(iso).toLocaleString("ko-KR", { timeZone: "Asia/Seoul", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "수신 기록 없음");

/** 24시간 연결 타임라인. 구간의 실제 길이로 그리고, 받지 못한 구간을 정상으로 늘리지 않는다 (빈 칸으로 남김). */
function ConnectionTimeline({ timeline }: { timeline: StatusTimeline }) {
  return (
    <TimelineChart
      title={`${timeline.hours}시간 연결 타임라인`}
      description={`${kst(timeline.from)} — ${kst(timeline.to)} · KST · 막대가 없는 칸은 상태를 받지 못한 시간`}
      from={timeline.from}
      to={timeline.to}
      rows={timeline.machines.map((m) => ({ label: `${m.label} · ${m.uptimePct == null ? "—" : `${m.uptimePct}%`}`, segments: m.segments.map((x) => ({ from: x.from, to: x.to, ok: x.connected })) }))}
      extra={
        <div className="ln-segments">
          {timeline.machines.map((m) => (
            <div key={m.machineId}>
              <strong>
                {m.label} · 가동률 {m.uptimePct == null ? "—" : `${m.uptimePct}%`} · 끊김 {m.disconnects}회
              </strong>
              {m.segments.map((x, i) => (
                <p key={i}>
                  {kst(x.from)} → {kst(x.to)} · {x.connected ? "연결" : "끊김"}
                </p>
              ))}
              {!m.segments.length && <p>기록 없음</p>}
            </div>
          ))}
        </div>
      }
    />
  );
}

export function MachineStatusTable({ rows, onSelect }: { rows: StatusMachine[]; onSelect: (machineId: string) => void }) {
  return (
    <Panel title="현장 상태">
      <Table>
        <thead>
          <tr>
            {["PC / 버전", "연결", "AI", "디바이스 준비", "처리 상태", "상세"].map((v) => (
              <th key={v}>{v}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id}>
              <td>
                <strong>{m.label}</strong>
                <small className="meta-crew-sub">{m.version ? `v${m.version}` : "-"}</small>
              </td>
              <td>
                <Badge tone={m.readiness === "unpaired" ? "neutral" : m.connected ? "teal" : "coral"}>{m.readiness === "unpaired" ? "미연결" : m.connected ? "온라인" : "연결 끊김"}</Badge>
              </td>
              <td>{aiText(m)}</td>
              <td>{m.connected ? `${m.devices.ready} / ${m.devices.attached}` : "확인 불가"}</td>
              <td>
                <Badge tone={m.busy && m.connected ? "blue" : "neutral"}>{!m.connected ? "확인 불가" : m.busy ? "처리 중" : "대기"}</Badge>
              </td>
              <td>
                <Button variant="secondary" size="sm" onClick={() => onSelect(m.id)}>
                  상태 보기
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
}

/** 디바이스 준비 카드: 서버가 아는 디바이스 + (데스크톱이면) 로컬 엔진이 본 배터리·키보드 등. */
export function DeviceCards({ customerId, machineId, local }: { customerId: string; machineId: string; local?: LocalStatus | null }) {
  const q = useMachine(customerId, machineId);
  const devices = q.data?.devices ?? [];
  const state = (q.data?.lastState ?? {}) as Record<string, any>;
  const extra = new Map<string, Record<string, any>>(
    [...(Array.isArray(state.deviceStates) ? state.deviceStates : []), ...(local?.devices ?? [])].map((d: Record<string, any>) => [d.serial, d]),
  );
  return (
    <Panel title="디바이스 준비 상태" extra={<Badge>{devices.length}대 연결</Badge>}>
      {devices.length === 0 ? (
        <InfoRow title="연결된 디바이스가 없습니다" description="USB 디버깅을 켜고 연결 시 '항상 허용'을 선택하세요." />
      ) : (
        <div className="lws-device-list">
          {devices.map((d) => {
            const x = extra.get(d.serial) ?? {};
            const approved = (x.state ?? d.state) === "device";
            // 잠금(밀어서 해제)·키보드는 작업 시작 때 자동 처리 → 승인되면 준비됨
            const ok = approved;
            return (
              <div className="lws-device" key={d.id}>
                <div className="lws-device-title">
                  <span>
                    <Smartphone size={17} />
                    <strong>{d.alias ?? d.model ?? d.serial}</strong>
                  </span>
                  <Badge tone={ok ? "teal" : "amber"}>{approved ? (ok ? "준비됨" : "확인 필요") : (deviceState[d.state]?.[0] ?? "확인 필요")}</Badge>
                </div>
                <small>
                  {d.model ?? "모델 정보 없음"} · {d.transport === "wifi" ? "Wi-Fi" : "USB"} 연결
                </small>
                <dl>
                  <div>
                    <dt>배터리</dt>
                    <dd>{x.batteryPct !== undefined ? `${x.batteryPct}%${x.charging ? " · 충전 중" : ""}` : "확인 대기"}</dd>
                  </div>
                  <div>
                    <dt>화면 / 잠금</dt>
                    <dd>{x.screenOn === undefined && x.locked === undefined ? "확인 대기" : `${x.screenOn ? "켜짐" : "꺼짐"} / ${x.locked ? "잠김 (작업 시 자동 해제)" : "해제"}`}</dd>
                  </div>
                  <div>
                    <dt>한글 키보드</dt>
                    <dd>{!approved ? "승인 후 확인" : x.keyboardReady === undefined ? "확인 대기" : x.keyboardReady ? "준비됨" : "작업 시작 시 자동 설치"}</dd>
                  </div>
                </dl>
                {!approved && <p className="lws-warning">디바이스에서 USB 디버깅 ‘항상 허용’을 선택해 주세요.</p>}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

/** 고객 웹·데스크톱의 읽기 전용 머신 상세 (작업 탭·네이티브 버튼 없음). */
export function ReadOnlyMachineView({ customerId, machineId, local }: { customerId: string; machineId: string; local?: LocalStatus | null }) {
  const status = useCustomerStatus(customerId);
  const [tab, setTab] = useState("상태");
  const m = status.data?.machines.find((x) => x.id === machineId);
  if (status.isLoading) return <Loading />;
  if (!m) return <Empty>머신을 찾을 수 없습니다.</Empty>;
  const disconnected = local ? local.socket !== "connected" : !m.connected;
  return (
    <>
      <div className="lws-service">
        <div>
          <strong>{m.label}</strong>
          <p>마지막 수신 {ago(local?.lastReceivedAt ?? m.lastSeenAt)}</p>
        </div>
        <Badge tone={disconnected ? "coral" : readinessText[m.readiness][1]}>{disconnected ? "연결 확인 필요" : readinessText[m.readiness][0]}</Badge>
      </div>
      <Tabs items={["상태", "디바이스"]} value={tab} onChange={setTab} />
      <div className={disconnected ? "mcc-stale" : ""}>
        {tab === "상태" && (
          <>
            <MetricGrid
              items={[
                { label: "서버", value: disconnected ? "연결 끊김" : "연결됨", icon: <Cloud /> },
                { label: "프로그램", value: disconnected ? "확인 불가" : m.version ? `v${m.version}` : "-", icon: <Cpu /> },
                { label: "AI", value: disconnected ? "확인 불가" : aiText(m), icon: <Zap /> },
                // 누르면 이 머신의 디바이스 탭 (2026-10-21)
                { label: "디바이스", value: disconnected ? "확인 불가" : `${m.devices.ready} / ${m.devices.attached}`, icon: <Smartphone />, onOpen: () => setTab("디바이스"), openLabel: "디바이스 탭에서 보기" },
              ]}
            />
            {m.issues.length > 0 && <InfoRow title="확인이 필요한 항목" description={issueText(m)} />}
            <StatusHistoryPanel path={`/c/${customerId}/machines/${machineId}/status-history`} title="24시간 연결 이력" />
          </>
        )}
        {tab === "디바이스" && <DeviceCards customerId={customerId} machineId={machineId} local={local} />}
      </div>
    </>
  );
}

/** 데스크톱 전용 "이 PC 설정" (네이티브). 웹에서는 렌더하지 않는다 (호출 수단도 없음). */
export function NativeSettingsView({ local, onUnpaired }: { local: LocalStatus | null; onUnpaired: () => void }) {
  const id = useId();
  const n = native();
  const [confirm, setConfirm] = useState<"" | "AI 로그아웃" | "프로그램 재시작" | "페어링 해제">("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);
  if (!n)
    return (
      <div className="mcc-empty">
        <ShieldCheck />
        <h3>접근할 수 없는 화면입니다</h3>
        <p>이 PC 설정은 연결된 데스크톱 앱에서만 사용할 수 있습니다.</p>
      </div>
    );
  const call = async (label: string, fn: () => Promise<{ ok: boolean; message?: string }>) => {
    setBusy(true);
    setResult("");
    try {
      const r = await fn();
      setResult(r.message ?? (r.ok ? `${label}을(를) 마쳤습니다.` : `${label}에 실패했습니다.`));
      return r.ok;
    } catch (e) {
      setResult(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const ai = local?.codex.loggedIn ?? false;
  const pending = (local?.devices ?? []).filter((d) => d.state !== "device");
  const theme = useThemeMode();
  return (
    <div className="lws-stack">
      {result && (
        <p className="lws-result" role="status">
          {result}
        </p>
      )}
      <Panel title="AI 계정" extra={<Badge tone={ai ? "teal" : "amber"}>{ai ? "로그인됨" : "로그인 필요"}</Badge>}>
        <InfoRow title="AI 연결" description={local?.codex.detail ?? "로그인은 이 PC의 브라우저에서 진행됩니다. 계정 비밀값은 화면에 표시하지 않습니다."}>
          <Button size="sm" disabled={busy} onClick={() => void call("AI 로그인", () => n.codexLogin())}>
            {ai ? "다시 로그인" : "AI 로그인"}
          </Button>
          {ai && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirm("AI 로그아웃")}>
              AI 로그아웃
            </Button>
          )}
        </InfoRow>
      </Panel>
      <Panel title="화면">
        <InfoRow title="테마" description="이 PC 의 앱 화면에만 적용됩니다. 시스템을 고르면 Windows 설정(라이트·다크)을 따릅니다.">
          <SegmentedControl
            label="테마"
            value={theme.mode}
            onChange={(v) => theme.save(v as ThemeMode)}
            options={[
              { value: "system", label: "시스템" },
              { value: "light", label: "라이트" },
              { value: "dark", label: "다크" },
            ]}
          />
        </InfoRow>
      </Panel>
      <Panel title="디바이스 준비">
        <InfoRow title="한글 키보드" description="승인된 디바이스에 설치합니다. 작업 시작 시 필요한 경우 자동으로 준비됩니다.">
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void call("한글 키보드 준비", () => n.setupPhone())}>
            키보드 설치
          </Button>
        </InfoRow>
        {pending.map((d) => (
          <InfoRow key={d.serial} title={`${d.model ?? d.serial} · USB 승인 대기`} description="디바이스에서 USB 디버깅 ‘항상 허용’을 선택해 주세요." />
        ))}
      </Panel>
      <Panel title="프로그램">
        <InfoRow title={`프로그램 ${local?.appVersion ? `v${local.appVersion}` : ""}`} description={local?.update?.message ?? "현재 실행 중인 프로그램의 업데이트를 확인합니다"}>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void call("업데이트 확인", () => n.checkUpdate())}>
            업데이트 확인
          </Button>
        </InfoRow>
        <InfoRow title="로그 폴더" description="이 PC에 보관된 진단 로그를 확인합니다">
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void call("로그 폴더 열기", () => n.openLogs())}>
            폴더 열기
          </Button>
        </InfoRow>
        <InfoRow title="프로그램 재시작" description="연결이 잠시 끊어지며 프로그램이 다시 시작됩니다">
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirm("프로그램 재시작")}>
            재시작
          </Button>
        </InfoRow>
      </Panel>
      <Panel title="PC 연결">
        <InfoRow title="페어링 해제" description="중앙에서도 이 PC 연결이 해제되고, 이 PC와 폰에 남은 지난 작업 기록·원고·캡처가 지워집니다. 다시 쓰려면 담당자가 발급한 새 코드가 필요합니다">
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirm("페어링 해제")}>
            페어링 해제
          </Button>
        </InfoRow>
      </Panel>
      <Dialog
        open={!!confirm}
        onOpenChange={(open) => !open && setConfirm("")}
        title={confirm}
        footer={
          <DialogActions
            secondary={{ label: "취소", onClick: () => setConfirm("") }}
            primary={{ label: confirm, tone: confirm === "페어링 해제" ? "danger" : "default", type: "submit", form: id, disabled: busy }}
          />
        }
      >
        <form
          id={id}
          onSubmit={(e) => {
            e.preventDefault();
            const what = confirm;
            setConfirm("");
            if (what === "AI 로그아웃") void call(what, () => n.codexLogout());
            if (what === "프로그램 재시작") void call(what, () => n.restart());
            if (what === "페어링 해제") void call(what, () => n.unpair()).then((ok) => ok && onUnpaired());
          }}
        >
          <p>{confirm === "페어링 해제" ? "이 PC와 고객사의 연결을 해제하고 코드 입력 화면으로 돌아갑니다." : `${confirm}을 진행할까요? 연결과 준비 상태가 달라질 수 있습니다.`}</p>
        </form>
      </Dialog>
    </div>
  );
}
